// CTSK-498 — is Document grouping live for this user's org?
// Both gates must be on: the API env flag (/meta/features → documentGrouping)
// and the per-org Settings → Documents "groupingEnabled" toggle. Also exposes
// showBulkConfirmationDialogs so the Bulk Edit modal honours the org setting.
// Any failure (no settings:view, offline) resolves to "off" — the feature
// stays hidden rather than half-working.

import { useQuery } from '@tanstack/react-query';
import { normalizeGroupingFolderSettings, type GroupingAutoNameMode } from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import { useFeatures } from '../lib/features.js';

interface DocumentsSettingsEnvelope {
  body?: {
    groupingEnabled?: unknown;
    showBulkConfirmationDialogs?: unknown;
    groupingIgnore?: unknown;
    groupingMaxFilesPerDrop?: unknown;
    groupingAutoNameFromFolder?: unknown;
  } | null;
}

export interface DocumentGroupingState {
  enabled: boolean;
  showBulkConfirmationDialogs: boolean;
  /** CTSK-501 — folder capture settings, defaulted and clamped (shared normaliser; the API clamps too). */
  groupingIgnore: string[];
  groupingMaxFilesPerDrop: number;
  groupingAutoNameFromFolder: GroupingAutoNameMode;
}

export function useDocumentGrouping(): DocumentGroupingState {
  const { documentGrouping } = useFeatures();
  const q = useQuery({
    queryKey: ['settings', 'documents'],
    queryFn: () => apiSingle<DocumentsSettingsEnvelope>('/settings/documents'),
    enabled: documentGrouping,
    staleTime: 30_000,
    retry: 0,
  });
  const body = q.data?.body ?? {};
  return {
    enabled: documentGrouping && body.groupingEnabled === true,
    showBulkConfirmationDialogs: body.showBulkConfirmationDialogs !== false,
    ...normalizeGroupingFolderSettings(body),
  };
}
